/**
 * Budgets by month (the Finance > Budgets grid) and budget-vs-actual.
 *
 * Each FY's budget is laid out like the organisation's budget spreadsheet:
 * coded lines are planned from the same smooth expectation the ledger's
 * actuals jitter around (so budget and actuals compare sensibly), and a
 * quarter of lines have no MYOB account code. Edits live in the tab's state.
 */
import { addDays, iso, now, ym } from '../../clock';
import { rng } from '../../rng';
import { getState, lastSyncedAt } from '../../state';
import { currentFy, fullMonth, monthActuals, monthFraction, r2 } from './ledger';

export interface BudgetLine {
    line_key: string;
    label: string;
    account_code: string | null;
    section: string | null;
    kind: 'line' | 'subtotal';
    sort_order: number;
    months: Record<string, number>;
    total: number;
}

type Formula = { type: 'section'; section: string } | { type: 'gross' } | { type: 'net' };

interface StoredLine extends BudgetLine {
    formula?: Formula;
}

interface BudgetYearState {
    updatedAt: string;
    /** `${line_key}:${month}` cells entered by hand. */
    manual: Set<string>;
    lines: StoredLine[];
}

/** `flat`: the account's FY expectation spread evenly (straight-line items such as depreciation). */
type Source = { account: string; factor?: number; flat?: boolean } | { pct: number };
type LineDef = [label: string, code: string | null, section: string, source: Source];
type SubtotalDef = [label: string, section: string, formula: Formula];

const INCOME = 'Income';
const COS = 'Cost of Sales';
const OPEX = 'Operating Expenses';
const SUMMARY = 'Summary';

/** Spreadsheet order. Strings are rows; subtotals carry a formula. */
const LAYOUT: (LineDef | SubtotalDef)[] = [
    ['NDIS – Disability Supports', '4-1100', INCOME, { account: '4-1100' }],
    ['NDIS – Support Coordination', '4-1300', INCOME, { account: '4-1300' }],
    ['Aged Care – Home Care Packages & CHSP', '4-1200', INCOME, { account: '4-1200' }],
    ['NIAA – Indigenous Advancement Strategy', '4-2000', INCOME, { account: '4-2000' }],
    ['DSS – Families & Safety Grants', '4-2100', INCOME, { account: '4-2100' }],
    ['NT Government Grants', '4-2200', INCOME, { account: '4-2200' }],
    ['SA Government Grants', '4-2300', INCOME, { account: '4-2300' }],
    ['WA Government Grants', '4-2400', INCOME, { account: '4-2400' }],
    ['Tjanpi Sales', '4-3000', INCOME, { account: '4-3000' }],
    ['Donations & Philanthropy', '4-4000', INCOME, { account: '4-4000' }],
    ['Fee for Service – Cultural Training', null, INCOME, { pct: 0.45 }],
    ['Vehicle & Accommodation Recoveries', null, INCOME, { pct: 0.2 }],
    ['Interest Income', '4-9000', INCOME, { account: '4-9000' }],
    ['Total Income', INCOME, { type: 'section', section: INCOME }],

    ['Direct Wages', '5-0150', COS, { account: '5-0150', factor: 0.995 }],
    ['Program Management Wages', '5-5100', COS, { account: '5-5100', factor: 0.995 }],
    ['Superannuation', '5-0300', COS, { account: '5-0300', factor: 0.995 }],
    ['Workers Compensation', '5-0400', COS, { account: '5-0400' }],
    ['Tjanpi Artist Payments', '5-0600', COS, { account: '5-0600' }],
    ['Client & Program Consumables', '5-0700', COS, { account: '5-0700', factor: 0.8 }],
    ['Child Nutrition Food Supplies', null, COS, { pct: 0.35 }],
    ['Ngangkari Consultation Fees', null, COS, { pct: 0.3 }],
    ['Interpreter Services', null, COS, { pct: 0.15 }],
    ['Bush Trips & Country Visits', null, COS, { pct: 0.25 }],
    ['Total Cost of Sales', COS, { type: 'section', section: COS }],

    ['Wages & Allowances', '6-5100', OPEX, { account: '6-5100', factor: 0.99 }],
    ['Superannuation', '6-5300', OPEX, { account: '6-5300', factor: 0.99 }],
    ['Rent & Outgoings', '6-0200', OPEX, { account: '6-0200', factor: 0.85 }],
    ['Staff Housing', null, OPEX, { pct: 0.55 }],
    ['Vehicle Leasing', '6-0510', OPEX, { account: '6-0510', factor: 0.9 }],
    ['Fuel', '6-0520', OPEX, { account: '6-0520' }],
    ['Travel & Charter Flights', '6-0600', OPEX, { account: '6-0600', factor: 0.85 }],
    ['Satellite & Remote Communications', null, OPEX, { pct: 0.18 }],
    ['Insurance', '6-0700', OPEX, { account: '6-0700' }],
    ['IT & Software', '6-0800', OPEX, { account: '6-0800', factor: 0.85 }],
    ['Training & Development', '6-1100', OPEX, { account: '6-1100' }],
    ['Recruitment & Advertising', null, OPEX, { pct: 0.12 }],
    ['Repairs & Maintenance', '6-1200', OPEX, { account: '6-1200' }],
    ['Depreciation', '6-1300', OPEX, { account: '6-1300', flat: true }],
    ['Audit & Professional Fees', '6-1400', OPEX, { account: '6-1400' }],
    ['Community Meetings & Governance', '6-1500', OPEX, { account: '6-1500' }],
    ['Uniforms & PPE', null, OPEX, { pct: 0.06 }],
    ['Printing & Stationery', null, OPEX, { pct: 0.05 }],
    ['Bank Fees', null, OPEX, { pct: 0.03 }],
    ['Subscriptions & Memberships', null, OPEX, { pct: 0.04 }],
    ['Freight', null, OPEX, { pct: 0.2 }],
    ['Contingency', null, OPEX, { pct: 0.75 }],
    ['Total Operating Expenses', OPEX, { type: 'section', section: OPEX }],

    ['Gross Profit', SUMMARY, { type: 'gross' }],
    ['Net Surplus / (Deficit)', SUMMARY, { type: 'net' }],
];

/** FY string for the FY ending in `fy`: 2027 → "2026-27". */
export function fyLabel(fy: number): string {
    return `${fy - 1}-${String(fy).slice(2)}`;
}

/** Calendar month (1..12) → `YYYY-MM` within FY "2026-27". */
function fyMonthKey(financialYear: string, month: number): string {
    const start = Number(financialYear.slice(0, 4));
    return `${month >= 7 ? start : start + 1}-${String(month).padStart(2, '0')}`;
}

export function slugify(label: string): string {
    return label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
}

function uniqueKey(lines: { line_key: string }[], label: string): string {
    const base = slugify(label) || 'line';
    let key = base;
    for (let n = 2; lines.some(l => l.line_key === key); n++) {
        key = `${base}_${n}`;
    }
    return key;
}

const MONTHS = [7, 8, 9, 10, 11, 12, 1, 2, 3, 4, 5, 6];

function buildYear(financialYear: string, manualCells: [string, number, number][], updatedAt: string): BudgetYearState {
    const expected: Record<number, Record<string, number>> = {};
    const revenue: Record<number, number> = {};
    for (const m of MONTHS) {
        expected[m] = fullMonth(fyMonthKey(financialYear, m), false);
        revenue[m] = Object.entries(expected[m])
            .filter(([c]) => c.startsWith('4-'))
            .reduce((s, [, v]) => s + v, 0);
    }

    const lines: StoredLine[] = [];
    LAYOUT.forEach((def, i) => {
        if (def.length === 3) {
            const [label, section, formula] = def;
            lines.push({ line_key: uniqueKey(lines, label), label, account_code: null, section, kind: 'subtotal', sort_order: i + 1, months: {}, total: 0, formula });
            return;
        }
        const [label, code, section, source] = def;
        const r = rng(`finance:budget:${financialYear}:${label}:${code}`);
        const months: Record<string, number> = {};
        const flat = 'account' in source && source.flat ? MONTHS.reduce((s, m) => s + expected[m][source.account], 0) / 12 : null;
        for (const m of MONTHS) {
            const v = flat !== null ? flat : 'account' in source ? expected[m][source.account] * (source.factor ?? 1) : (revenue[m] * source.pct) / 100;
            months[String(m)] = Math.round(v * ('pct' in source ? r.float(0.96, 1.04, 3) : flat !== null ? 1 : r.float(0.985, 1.015, 4)));
        }
        lines.push({ line_key: uniqueKey(lines, label), label, account_code: code, section, kind: 'line', sort_order: i + 1, months, total: 0 });
    });

    const manual = new Set<string>();
    for (const [key, month, factor] of manualCells) {
        const line = lines.find(l => l.line_key === key);
        if (line) {
            line.months[String(month)] = Math.round(line.months[String(month)] * factor);
            manual.add(`${key}:${month}`);
        }
    }
    return { updatedAt, manual, lines };
}

function store(): Record<string, BudgetYearState> {
    return getState('finance:budgets', () => {
        const fy = currentFy();
        const current = fyLabel(fy);
        const previous = fyLabel(fy - 1);
        const edited = addDays(now(), -4);
        edited.setHours(9, 5, 11, 0);
        const lastJune = new Date(fy - 1, 5, 24, 10, 12, 40);
        return {
            [current]: buildYear(
                current,
                [
                    ['vehicle_leasing', 7, 1.12],
                    ['vehicle_leasing', 8, 1.12],
                    ['training_development', 9, 1.35],
                    ['contingency', 10, 0.8],
                    ['contingency', 11, 0.8],
                    ['contingency', 12, 0.8],
                ],
                iso(edited),
            ),
            [previous]: buildYear(
                previous,
                [
                    ['staff_housing', 1, 1.2],
                    ['staff_housing', 2, 1.2],
                ],
                iso(lastJune),
            ),
        };
    });
}

function sumMonths(lines: StoredLine[]): Record<string, number> {
    const out: Record<string, number> = {};
    for (const m of MONTHS) {
        out[String(m)] = r2(lines.reduce((s, l) => s + (l.months[String(m)] ?? 0), 0));
    }
    return out;
}

/** Lines with subtotal rows recomputed from the current figures. */
function materialise(state: BudgetYearState): BudgetLine[] {
    const lines = [...state.lines].sort((a, b) => a.sort_order - b.sort_order);
    const plain = lines.filter(l => l.kind === 'line');
    const bySection = (s: string) => sumMonths(plain.filter(l => l.section === s));
    const income = bySection(INCOME);
    const cos = bySection(COS);
    const opex = bySection(OPEX);

    return lines.map(l => {
        let months = { ...l.months };
        if (l.kind === 'subtotal' && l.formula) {
            const f = l.formula;
            if (f.type === 'section') {
                months = bySection(f.section);
            } else {
                months = {};
                for (const m of MONTHS) {
                    const k = String(m);
                    months[k] = r2(income[k] - cos[k] - (f.type === 'net' ? opex[k] : 0));
                }
            }
        }
        const total = r2(Object.values(months).reduce((s, v) => s + v, 0));
        return {
            line_key: l.line_key,
            label: l.label,
            account_code: l.account_code,
            section: l.section,
            kind: l.kind,
            sort_order: l.sort_order,
            months,
            total,
        };
    });
}

/** GET /api/budget/years. */
export function budgetYears(): { years: string[] } {
    return { years: Object.keys(store()).sort().reverse() };
}

export function hasBudget(financialYear: string): boolean {
    const y = store()[financialYear];
    return !!y && y.lines.length > 0;
}

/** GET /api/budget/{fy}, or null when no budget is stored. Board keeps only the allowlisted fields. */
export function budgetYear(financialYear: string, board = false): Record<string, unknown> | null {
    const state = store()[financialYear];
    if (!state || !state.lines.length) {
        return null;
    }
    const lines = materialise(state);
    if (board) {
        return {
            lines: lines.map(({ line_key, label, account_code, section, kind, months }) => ({ line_key, label, account_code, section, kind, months })),
            updated_at: state.updatedAt,
            manual_edits: state.manual.size,
        };
    }
    return { financial_year: financialYear, lines, updated_at: state.updatedAt, manual_edits: state.manual.size };
}

export type BudgetResult = { status: number; body: unknown };

const err = (status: number, error: string): BudgetResult => ({ status, body: { error } });

/** Parse `{ "7": 12000, ... }` into calendar month → amount, or an error string. */
function readMonths(raw: unknown): Record<number, number> | string {
    if (raw == null) {
        return {};
    }
    if (typeof raw !== 'object') {
        return 'months must be an object keyed by calendar month (1-12).';
    }
    const out: Record<number, number> = {};
    for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
        const m = Number(k);
        if (!Number.isInteger(m) || m < 1 || m > 12) {
            return `Invalid month "${k}" — expected 1-12.`;
        }
        const n = Number(v);
        if (v === null || v === '' || Number.isNaN(n)) {
            return `Amount for month ${k} must be a number.`;
        }
        out[m] = r2(n);
    }
    return out;
}

/** POST /api/budget/{fy}/line/{key}. */
export function saveBudgetLine(financialYear: string, lineKey: string, body: any): BudgetResult {
    const state = store()[financialYear];
    const line = state?.lines.find(l => l.line_key === lineKey);
    if (!state || !line) {
        return err(404, `No budget line "${lineKey}" for ${financialYear}.`);
    }
    if (line.kind === 'subtotal') {
        return err(409, `"${line.label}" is a subtotal row and cannot be edited directly.`);
    }
    const months = readMonths(body?.months);
    if (typeof months === 'string') {
        return err(400, months);
    }
    for (const [m, v] of Object.entries(months)) {
        line.months[m] = v;
        state.manual.add(`${lineKey}:${m}`);
    }
    if (typeof body?.label === 'string' && body.label.trim()) {
        line.label = body.label.trim();
    }
    if (body && 'account_code' in body) {
        line.account_code = body.account_code ? String(body.account_code).trim() : null;
    }
    if (body && 'section' in body) {
        line.section = body.section ? String(body.section).trim() : null;
    }
    state.updatedAt = iso(now());
    return { status: 200, body: { financial_year: financialYear, line_key: lineKey, months_updated: Object.keys(months).length } };
}

/** POST /api/budget/{fy}/line. */
export function addBudgetLine(financialYear: string, body: any): BudgetResult {
    const label = typeof body?.label === 'string' ? body.label.trim() : '';
    if (!label) {
        return err(400, 'label is required.');
    }
    if (!/^\d{4}-\d{2}$/.test(financialYear)) {
        return err(400, 'financialYear must look like 2026-27.');
    }
    if (!slugify(label)) {
        return err(400, 'label must contain at least one alphanumeric character.');
    }
    const months = readMonths(body?.months);
    if (typeof months === 'string') {
        return err(400, months);
    }
    const all = store();
    const state = (all[financialYear] ??= { updatedAt: iso(now()), manual: new Set<string>(), lines: [] });
    const key = uniqueKey(state.lines, label);
    const sortOrder = state.lines.reduce((mx, l) => Math.max(mx, l.sort_order), 0) + 1;
    const lineMonths: Record<string, number> = {};
    for (let m = 1; m <= 12; m++) {
        lineMonths[String(m)] = months[m] ?? 0;
        state.manual.add(`${key}:${m}`);
    }
    state.lines.push({
        line_key: key,
        label,
        account_code: body?.account_code ? String(body.account_code).trim() : null,
        section: body?.section ? String(body.section).trim() : null,
        kind: 'line',
        sort_order: sortOrder,
        months: lineMonths,
        total: 0,
    });
    state.updatedAt = iso(now());
    return { status: 201, body: { financial_year: financialYear, line_key: key, label } };
}

/** POST /api/budget/{fy}/line/{key}/delete. */
export function deleteBudgetLine(financialYear: string, lineKey: string): BudgetResult {
    const state = store()[financialYear];
    const line = state?.lines.find(l => l.line_key === lineKey);
    if (!state || !line) {
        return err(404, `No budget line "${lineKey}" for ${financialYear}.`);
    }
    if (line.kind === 'subtotal') {
        return err(409, 'Subtotal rows come from the spreadsheet and cannot be deleted here.');
    }
    state.lines = state.lines.filter(l => l !== line);
    for (const cell of [...state.manual]) {
        if (cell.startsWith(`${lineKey}:`)) {
            state.manual.delete(cell);
        }
    }
    state.updatedAt = iso(now());
    return { status: 204, body: null };
}

/** POST /api/budget/{fy}/seed-from/{source}[?copy_amounts=1]. */
export function seedBudgetFrom(financialYear: string, sourceYear: string, copyAmounts: boolean): BudgetResult {
    const valid = (fy: string) => /^\d{4}-\d{2}$/.test(fy);
    if (!valid(financialYear) || !valid(sourceYear)) {
        return err(400, 'Financial years must look like 2026-27.');
    }
    if (financialYear === sourceYear) {
        return err(400, 'Source and target years must differ.');
    }
    const all = store();
    const source = all[sourceYear];
    if (!source || !source.lines.length) {
        return err(404, `No budget stored for ${sourceYear} to copy from.`);
    }
    if (all[financialYear]?.lines.length) {
        return err(409, `${financialYear} already has budget lines — delete them first or edit in place.`);
    }
    all[financialYear] = {
        updatedAt: iso(now()),
        manual: new Set<string>(),
        lines: source.lines.map(l => ({
            ...l,
            months: Object.fromEntries(Object.entries(l.months).map(([m, v]) => [m, copyAmounts ? v : 0])),
        })),
    };
    return { status: 201, body: { financial_year: financialYear, copied_from: sourceYear, lines_created: source.lines.length, amounts_copied: copyAmounts } };
}

/** GET /api/budget/{fy}/vs-actual[?months=7,8,9] (BudgetComparisonService::compare). Null if no budget. */
export function budgetVsActual(financialYear: string, months: number[] | null = null): Record<string, unknown> | null {
    const state = store()[financialYear];
    if (!state || !state.lines.length) {
        return null;
    }
    const current = ym(now());
    const actualMonths = MONTHS.filter(m => fyMonthKey(financialYear, m) <= current);
    const window = months ?? actualMonths;

    const actuals: Record<string, number> = {};
    for (const m of actualMonths) {
        if (!window.includes(m)) {
            continue;
        }
        for (const [code, v] of Object.entries(monthActuals(fyMonthKey(financialYear, m)) ?? {})) {
            actuals[code] = (actuals[code] ?? 0) + v;
        }
    }

    const budgets: Record<string, number> = {};
    const unmapped: { label: string; section: string | null; total: number }[] = [];
    for (const l of state.lines) {
        if (l.kind !== 'line') {
            continue;
        }
        // The current month's actuals are month-to-date, so compare against the same share of its budget.
        const amount = window.reduce((s, m) => s + (l.months[String(m)] ?? 0) * (monthFraction(fyMonthKey(financialYear, m)) || 1), 0);
        if (l.account_code) {
            budgets[l.account_code] = (budgets[l.account_code] ?? 0) + amount;
        } else {
            unmapped.push({ label: l.label, section: l.section, total: r2(amount) });
        }
    }
    unmapped.sort((a, b) => a.label.localeCompare(b.label));

    const rows = Object.entries(budgets)
        .map(([code, budget]) => {
            const actual = code in actuals ? r2(actuals[code]) : null;
            return {
                account_code: code,
                budget: r2(budget),
                actual,
                variance: actual === null ? null : r2(actual - budget),
                variance_percent: actual !== null && Math.abs(budget) >= 0.005 ? Math.round(((actual - budget) / Math.abs(budget)) * 1000) / 10 : null,
                has_actual: actual !== null,
            };
        })
        .sort((a, b) => a.account_code.localeCompare(b.account_code));
    const unbudgeted = Object.entries(actuals)
        .filter(([code, v]) => !(code in budgets) && Math.abs(v) >= 0.005)
        .map(([code, v]) => ({ account_code: code, actual: r2(v) }))
        .sort((a, b) => a.account_code.localeCompare(b.account_code));

    const totalBudget = rows.reduce((s, r) => s + r.budget, 0);
    const totalActual = rows.reduce((s, r) => s + (r.actual ?? 0), 0);
    return {
        financial_year: financialYear,
        months_compared: window,
        months_with_actuals: actualMonths.map(m => fyMonthKey(financialYear, m)),
        rows,
        unmapped,
        unbudgeted,
        totals: { budget: r2(totalBudget), actual: r2(totalActual), variance: r2(totalActual - totalBudget) },
        budget_updated_at: state.updatedAt,
        actuals_synced_at: lastSyncedAt('finance'),
    };
}
